import { Routes, Route, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { Sidebar } from './components/layout/Sidebar';
import { Header } from './components/layout/Header';
import { Dashboard } from './pages/Dashboard';
import { CallMonitor } from './pages/CallMonitor';
import { Analytics } from './pages/Analytics';
import { Calls } from './pages/Calls';
import { VoiceCall } from './pages/VoiceCall';
import { Agents } from './pages/Agents';
import { Transcripts } from './pages/Transcripts';
import { System } from './pages/System';
import { Settings } from './pages/Settings';
import { Login } from './pages/Login';
import { Landing } from './pages/Landing';
import { Company } from './pages/Company';
import { useWebSocket } from './hooks/useWebSocket';
import { useState, useEffect } from 'react';
import { isAuthenticated, isDemo } from './lib/auth';

// ── Authenticated console (the operator dashboard, mounted under /app) ──────────
function Console() {
  const location = useLocation();
  const demo = isDemo();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const { status, latency } = useWebSocket({
    url: 'wss://api.vokentra-ai.example.com/v1/stream',
    autoConnect: true,
    onMessage: () => {},
    onConnect: () => {},
    onDisconnect: () => {},
  });

  useEffect(() => {
    const handleResize = () => { if (window.innerWidth < 1024) setSidebarCollapsed(true); };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="min-h-screen bg-background text-text-primary">
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute inset-0 bg-grid-pattern opacity-50" />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse 80% 50% at 50% -20%, rgba(230, 0, 0, 0.08) 0%, transparent 60%)' }} />
        <div className="absolute bottom-0 left-0 right-0 h-[400px]" style={{ background: 'radial-gradient(ellipse 60% 100% at 50% 100%, rgba(230, 0, 0, 0.04) 0%, transparent 70%)' }} />
      </div>

      <Sidebar className="z-40" />

      <div className="relative z-10 transition-all duration-300" style={{ marginLeft: sidebarCollapsed ? '72px' : '240px' }}>
        <Header wsStatus={status} wsLatency={latency} className="sticky top-0 z-30" />
        <main className="min-h-[calc(100vh-64px)]">
          <AnimatePresence mode="wait">
            <Routes location={location} key={location.pathname}>
              <Route index element={<Dashboard />} />
              <Route path="voice" element={<VoiceCall />} />
              <Route path="transcripts" element={<Transcripts />} />
              {/* Admin-only pages — demo account is redirected to the dashboard */}
              <Route path="settings" element={demo ? <Navigate to="/app" replace /> : <Settings />} />
              <Route path="calls" element={demo ? <Navigate to="/app" replace /> : <Calls />} />
              <Route path="monitor" element={demo ? <Navigate to="/app" replace /> : <CallMonitor />} />
              <Route path="analytics" element={demo ? <Navigate to="/app" replace /> : <Analytics />} />
              <Route path="agents" element={demo ? <Navigate to="/app" replace /> : <Agents />} />
              <Route path="system" element={demo ? <Navigate to="/app" replace /> : <System />} />
              <Route path="*" element={<Navigate to="/app" replace />} />
            </Routes>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
}

function LoginRoute({ onSuccess }: { onSuccess: () => void }) {
  const navigate = useNavigate();
  if (isAuthenticated()) return <Navigate to="/app" replace />;
  return <Login onSuccess={() => { onSuccess(); navigate('/app'); }} />;
}

function App() {
  const [authed, setAuthed] = useState(isAuthenticated());

  return (
    <Routes>
      {/* Public marketing site */}
      <Route path="/" element={<Landing />} />
      <Route path="/company" element={<Company />} />
      <Route path="/login" element={<LoginRoute onSuccess={() => setAuthed(true)} />} />

      {/* Gated operator console */}
      <Route path="/app/*" element={authed || isAuthenticated() ? <Console /> : <Navigate to="/login" replace />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
